import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import Button from 'components/Button';
import { usePermissionService } from 'services/permission';
import { Permission } from 'types/permission';
import styles from './Permission.module.scss';

interface AddPermissionModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (permission: Permission) => void;
  onError: (message: string) => void;
}

const AddPermissionModal: React.FC<AddPermissionModalProps> = ({
  show,
  onHide,
  onSuccess,
  onError,
}) => {
  const permissionService = usePermissionService();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    key: '',
    name: '',
    description: '',
  });

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!show) {
      setFormData({ key: '', name: '', description: '' });
    }
  }, [show]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const newPermission = await permissionService.createPermission(formData);
      onSuccess(newPermission);
    } catch (error) {
      console.error('Failed to create permission:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to create permission. Please try again.';
      onError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Permission</Modal.Title>
      </Modal.Header>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Modal.Body>
          <div className={styles.formGroup}>
            <label className={styles.label}>Key</label>
            <input
              type="text"
              name="key"
              className={styles.input}
              value={formData.key}
              onChange={handleChange}
              placeholder="e.g. USER_VIEW"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Name</label>
            <input
              type="text"
              name="name"
              className={styles.input}
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. View Users"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Description</label>
            <textarea
              name="description"
              className={styles.textarea}
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter permission description..."
              rows={3}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={onHide}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Permission'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AddPermissionModal;
